import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { colors, fonts } from '../components/UI'

// Login only. One shared TIC identity: magic link is primary, password is an
// always-available fallback, and the first login anywhere prompts the user to
// set a password once (handled in App after a session exists). There is no
// public "create account" here - accounts come into being when a buyer opens
// their first sign-in link, and entitlement is checked after login.
export default function AuthPage() {
  const { signIn, resetPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  const cleanEmail = () => email.trim().toLowerCase()
  const validEmail = (e) => !!e && e.indexOf('@') > 0

  async function sendMagicLink() {
    const e = cleanEmail()
    setError(''); setInfo('')
    if (!validEmail(e)) { setError('Enter a valid email address.'); return }
    setLoading(true)
    const { error } = await supabase.auth.signInWithOtp({
      email: e,
      options: { emailRedirectTo: window.location.origin },
    })
    setLoading(false)
    if (error) { setError(error.message); return }
    setSent(true)
  }

  async function signInWithPassword() {
    const e = cleanEmail()
    setError(''); setInfo('')
    if (!validEmail(e)) { setError('Enter a valid email address.'); return }
    if (!password) { setError('Enter your password.'); return }
    setLoading(true)
    const { error } = await signIn(e, password)
    setLoading(false)
    // On success the auth listener in App picks up the new session.
    if (error) setError(error.message)
  }

  async function forgot() {
    const e = cleanEmail()
    setError(''); setInfo('')
    if (!validEmail(e)) { setError('Enter your email first, then tap forgot password.'); return }
    await resetPassword(e)
    setInfo('If that email has an account, a reset link is on its way.')
  }

  const label = { display: 'block', fontSize: '14px', fontWeight: 600, color: colors.muted, marginBottom: '6px', fontFamily: fonts.body }
  const input = { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: colors.text, fontSize: '16px', fontFamily: fonts.body, padding: '13px 16px', outline: 'none', boxSizing: 'border-box', WebkitAppearance: 'none' }
  const primaryBtn = (busy) => ({ width: '100%', padding: '15px', background: busy ? 'rgba(201,151,43,0.5)' : colors.gold, border: 'none', borderRadius: '12px', fontFamily: fonts.heading, fontSize: '14px', fontWeight: 700, color: colors.onAccent, cursor: busy ? 'wait' : 'pointer', letterSpacing: '-0.01em' })
  const linkBtn = { background: 'none', border: 'none', fontSize: '14px', fontWeight: 500, color: colors.muted, cursor: 'pointer', padding: '6px' }
  const pageWrap = { minHeight: '100vh', minHeight: '100dvh', background: colors.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 20px' }

  const Logo = () => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '32px' }}>
      <div style={{ width: '3px', flexShrink: 0, alignSelf: 'stretch', background: colors.gold, borderRadius: '2px' }} />
      <div>
        <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300, fontSize: '14px', letterSpacing: '0.38em', color: `rgba(${colors.textRgb},0.4)`, marginBottom: '2px' }}>THE</div>
        <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: '34px', letterSpacing: '0.03em', color: colors.text, lineHeight: 1 }}>INVESTING</div>
        <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 300, fontSize: '14px', letterSpacing: '0.18em', color: colors.gold, lineHeight: 1.3 }}>COUPLE</div>
        <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: '13px', letterSpacing: '0.22em', color: `rgba(${colors.textRgb},0.72)`, borderTop: '1px solid rgba(201,151,43,0.2)', paddingTop: '8px', marginTop: '6px' }}>TRACKER</div>
      </div>
    </div>
  )

  // ── Magic link sent confirmation ──
  if (sent) {
    return (
      <div style={pageWrap}>
        <Logo />
        <div style={{ width: '100%', maxWidth: '400px', background: colors.card, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '20px', padding: '28px 24px', textAlign: 'center', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur }}>
          <div style={{ fontSize: '30px', marginBottom: '12px' }}>📩</div>
          <div style={{ fontFamily: fonts.heading, fontSize: '18px', fontWeight: 700, color: colors.text, letterSpacing: '0.02em', marginBottom: '10px' }}>Check your inbox</div>
          <p style={{ fontFamily: fonts.body, fontSize: '14px', color: colors.muted, lineHeight: 1.6, margin: '0 0 20px' }}>
            We have sent a sign-in link to <strong style={{ color: colors.text }}>{cleanEmail()}</strong>. Open it on this device to continue. You can close this once you are in.
          </p>
          <button onClick={() => { setSent(false); setError(''); setInfo('') }} style={{ ...linkBtn, color: colors.muted }}>
            ← Use a different email
          </button>
        </div>
      </div>
    )
  }

  // ── Login ──
  return (
    <div style={pageWrap}>
      <Logo />
      <div style={{ width: '100%', maxWidth: '400px', background: colors.card, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '20px', padding: '24px', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur }}>

        <div style={{ fontFamily: fonts.heading, fontSize: '20px', fontWeight: 700, color: colors.text, letterSpacing: '0.02em', marginBottom: '6px' }}>Sign in</div>
        <p style={{ fontFamily: fonts.body, fontSize: '14px', color: colors.muted, lineHeight: 1.6, margin: '0 0 20px' }}>
          Use the email you bought with. We will email you a one-time sign-in link, or you can use your password if you have set one.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={label}>Email</label>
            <input style={input} type="email" inputMode="email" placeholder="you@example.com" value={email} onChange={e => { setEmail(e.target.value); setError('') }} autoComplete="email" onKeyDown={e => { if (e.key === 'Enter' && !showPw) sendMagicLink() }} />
          </div>

          {showPw && (
            <div>
              <label style={label}>Password</label>
              <input style={input} type="password" placeholder="Your password" value={password} onChange={e => { setPassword(e.target.value); setError('') }} autoComplete="current-password" onKeyDown={e => { if (e.key === 'Enter') signInWithPassword() }} />
            </div>
          )}

          {error && (
            <div style={{ fontSize: '14px', color: colors.red, padding: '10px 14px', background: 'rgba(224,82,82,0.1)', border: '1px solid rgba(224,82,82,0.2)', borderRadius: '10px', fontWeight: 500 }}>{error}</div>
          )}
          {info && (
            <div style={{ fontSize: '14px', color: colors.green, padding: '10px 14px', background: 'rgba(61,184,122,0.1)', border: '1px solid rgba(61,184,122,0.2)', borderRadius: '10px', fontWeight: 500 }}>{info}</div>
          )}

          {!showPw ? (
            <>
              <button onClick={sendMagicLink} disabled={loading} style={primaryBtn(loading)}>
                {loading ? 'Sending...' : 'Email me a sign-in link'}
              </button>
              <button onClick={() => { setShowPw(true); setError(''); setInfo('') }} style={linkBtn}>
                Use a password instead
              </button>
            </>
          ) : (
            <>
              <button onClick={signInWithPassword} disabled={loading} style={primaryBtn(loading)}>
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <button onClick={() => { setShowPw(false); setPassword(''); setError(''); setInfo('') }} style={linkBtn}>
                  ← Use a sign-in link
                </button>
                <button onClick={forgot} style={linkBtn}>
                  Forgot password?
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
